import { type StructuredText as StructuredTextType } from 'datocms-structured-text-utils'
import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React from 'react'

import BackButton from '@/components/back-button/back-button'
import Form from '@/components/form/form'
import Header from '@/components/header/header'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import TableContent from '@/components/models/paper/tablecontent/tablecontent'
import Persons from '@/components/persons/persons'
import Related from '@/components/related/related'
import { useSiteMetadata } from '@/components/root-element'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import routes from '@/internal/constants/routes'
import { type Download } from '@/internal/types/form'
import Layout from '@/layouts/layout'
import { sortRelatedContent } from '@/utils/sort'
import { generateUrlFromNode } from '@/utils/url'

import * as styles from './paper.module.scss'

export type DataProps = {
  datoCmsPctPaper: Queries.PaperQuery['datoCmsPctPaper']
  allDatoCmsPctPaper: Queries.PaperQuery['allDatoCmsPctPaper']
  allDatoCmsPctPost: Queries.PaperQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.PaperQuery['allDatoCmsPctInterview']
  allDatoCmsPctDialog: Queries.PaperQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.PaperQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.PaperQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.PaperQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.PaperQuery['allDatoCmsPctExternalContent']
  allDatoCmsPctEvent: Queries.PaperQuery['allDatoCmsPctEvent']
}

export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctPaper },
  location
}) => {
  return (
    <>
      <Seo location={location} data={datoCmsPctPaper} />
      <Microdata data={datoCmsPctPaper} location={location} />
    </>
  )
}

const Paper: React.FC<PageProps<DataProps>> = ({
  data: {
    datoCmsPctPaper,
    allDatoCmsPctPaper,
    allDatoCmsPctPost,
    allDatoCmsPctInterview,
    allDatoCmsPctDialog,
    allDatoCmsPctProject,
    allDatoCmsPctEpisode,
    allDatoCmsPctPage,
    allDatoCmsPctExternalContent,
    allDatoCmsPctEvent
  },
  location
}) => {
  const { settings } = useSiteMetadata()
  if (!datoCmsPctPaper) return null
  const {
    externalName,
    headline,
    authors,
    cover,
    topic,
    tags,
    chapters,
    content,
    originalId,
    relatedPapers
  } = datoCmsPctPaper
  const {
    'text-back-allpapers': textBackAllpapers,
    'title-table-contents': titleTableContents,
    'title-about-authors': titleAboutAuthors
  } = settings

  const related = sortRelatedContent({
    results: 3,
    nodesGroups: [
      allDatoCmsPctPaper?.nodes as Card[],
      [
        ...(allDatoCmsPctProject?.nodes ?? []),
        ...(allDatoCmsPctPost?.nodes ?? []),
        ...(allDatoCmsPctInterview?.nodes ?? []),
        ...(allDatoCmsPctDialog?.nodes ?? []),
        ...(allDatoCmsPctEpisode?.nodes ?? []),
        ...(allDatoCmsPctPage?.nodes ?? []),
        ...(allDatoCmsPctExternalContent?.nodes ?? []),
        ...(allDatoCmsPctEvent?.nodes ?? [])
      ]
    ]
  })

  const persons = authors?.map((author) => author?.person)

  const downloads = [
    {
      originalId,
      externalName: externalName ?? '',
      required: true
    } as Download
  ]
  if (Array.isArray(relatedPapers)) {
    downloads.push(...(relatedPapers as unknown as Download[]))
  }
  return (
    <Layout location={location} shareButtonsScrollTop={150}>
      <Header
        title={externalName ?? ''}
        description={headline ?? ''}
        titleIsUppercase
      />
      <div className="pt-4 pb-3">
        <div className="container">
          <div className="row mb-4">
            <div className="col-lg-6">
              <BackButton
                url={routes.papersRoute}
                label={textBackAllpapers as string}
                className={styles.linkback}
              />
              <div className={styles.imageportada}>
                <Image image={cover} />
              </div>
            </div>
            <div
              className={`col-lg-6 pl-lg-5 ${styles.colform} column-form`}
              id="form-col"
            >
              <div className={styles.anchorForm} id="form" />
              {originalId && (
                <Form
                  subtitle={datoCmsPctPaper.externalName ?? ''}
                  values={{
                    form: 'paperDetail',
                    downloads
                  }}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div className={`pb-5 pt-5 ${styles.topics}`}>
                {topic && (
                  <div className={styles.topictag}>
                    <Link to={generateUrlFromNode(topic)}>
                      {topic.externalName}
                    </Link>
                  </div>
                )}
                {tags?.map(
                  (item) =>
                    item && (
                      <div key={item.id}>
                        <Link to={generateUrlFromNode(item)}>
                          {item.externalName}
                        </Link>
                      </div>
                    )
                )}
              </div>
              <TableContent title={titleTableContents} contents={chapters} />
              <div>
                <StructuredTextDefault
                  content={content as unknown as StructuredTextType}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Persons persons={persons} isDark title={titleAboutAuthors as string} />

      <Related related={related} />
    </Layout>
  )
}

export default Paper

export const query = graphql`
  query Paper($id: String, $related: [String] = "") {
    datoCmsPctPaper(id: { eq: $id }) {
      ...PctPaperDetail
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }

    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }

    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
  }

  fragment PctPaperDetail on DatoCmsPctPaper {
    __typename
    originalId
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    meta {
      firstPublishedAt
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    seo {
      title
      description
      image {
        url
      }
    }
    id
    externalName
    excerpt
    headline
    image {
      gatsbyImageData
      alt
    }
    card: image {
      ...imgCard
    }
    cover {
      alt
      gatsbyImageData(width: 600, layout: FULL_WIDTH)
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    chapters {
      id
      name
      description
    }
    content {
      value
      links {
        __typename
        ... on DatoCmsPctDialog {
          ...PctDialogCardInline
        }
        ... on DatoCmsPctTopic {
          ...PctTopicCardInline
        }
        ... on DatoCmsPctPaper {
          ...PctPaperCardInline
        }
        ... on DatoCmsPctInternalPage {
          ...PctInternalPageCardInline
        }
        ... on DatoCmsPctPost {
          ...PctPostCardInline
        }
        ... on DatoCmsPctInterview {
          ...PctInterviewCardInline
        }
        ... on DatoCmsPctProject {
          ...PctProjectCardInline
        }
        ... on DatoCmsPctPage {
          ...PctPageCardInline
        }
        ... on DatoCmsPctVision {
          ...PctVisionCardInline
        }
        ... on DatoCmsPctTag {
          ...PctTagCardInline
        }
        ... on DatoCmsPctReport {
          ...PctReportCardInline
        }
        ... on DatoCmsPctLink {
          ...PctLink
        }
      }
      blocks {
        __typename
        ... on DatoCmsImage {
          ...ImageInline
        }
        ... on DatoCmsVideo {
          ...VideoInline
        }
        ... on DatoCmsEmbed {
          ...EmbedInline
        }
      }
    }
    authors {
      ...Author
    }
    relatedPapers {
      __typename
      ... on DatoCmsPctPaper {
        originalId
        externalName
      }
    }
  }
`
